import { useMemo, useState } from "react";
import {Box, Button, Paper, Typography, capitalize, useTheme, useMediaQuery} from "@mui/material";
import { DataRequestState, DataTable, useDataRequest, useDebouncedFilter, type DataRequestParams } from "../../data";
import type { GridColDef } from "@mui/x-data-grid";
import type { FreeDay } from "../../../api";
import { useTranslation } from "react-i18next";
import FilterTableFreedays, {FreedaysFilter} from "./FilterTableFreedays";
import {aufgabenStyles} from "../../../styles/aufgaben/aufgaben.styles";
import { permissions } from "../../../permissions";
import MultipleGuard from "../../guards/MultipleGuard";
import FreedaysBulkCreateDialog from "../dialogs/FreedaysBulkCreateDialog";
import { useAddMandantFreedays } from "../../../client/hooks";
import {useFreedaysHandling} from "../../../hooks/freedays/useFreedaysHandling";
import {useSearchParams} from "react-router-dom";
import DeleteFreeDaysButton from "../DeleteFreedayButton";
import {usePermissions} from "../../PermissionsContext";
import {useQueryClient} from "react-query";
import {enqueueSnackbar} from "notistack";


interface TableFreeDaysProps{
  mandantId: string
}

export default function TableFreedays({mandantId}: TableFreeDaysProps) {
  const { t: translation } = useTranslation("freedays");
  const [searchParams] = useSearchParams();
  const [openBulkCreate, setOpenBulkCreate] = useState(false);
  const addMandantFreedays = useAddMandantFreedays();
  const { loadFreedays } = useFreedaysHandling(mandantId);
  const queryClient = useQueryClient();

  const request: DataRequestState<FreedaysFilter> = useDataRequest<FreedaysFilter>({
    filter: {
      filtered: searchParams.get("filter") ?? "",
      search: searchParams.get("search") ?? "",
      countryCode: searchParams.get("countryCode") ?? "",
      year: searchParams.get("year") ? parseInt(searchParams.get("year") as string, 10) : undefined,
    },
  });

  const [{search, year, countryCode}, setField] = useDebouncedFilter(request);

  const parseInitialFilters = (raw: string | null) => {
        if (!raw) return undefined;
        try {
            return JSON.parse(raw);
        } catch {
            return undefined;
        }
    };
  return (
          <Paper sx={{
            marginTop: 4,
            padding: 2,
            paddingBottom: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            <Box sx={aufgabenStyles.tableAufgaben.headerBoxWrapper}>
              <Typography variant="h5">{capitalize(translation("freedays"))}</Typography>
              <MultipleGuard permissions={[permissions.freedays.create, permissions.freedays.own_create]}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setOpenBulkCreate(true)}
                >
                  {capitalize(translation("freedays-bulk-create"))}
                </Button>
              </MultipleGuard>
            </Box>
            <FilterTableFreedays
              setField={setField}
              search={search}
              year={year}
              countryCode={countryCode ?? ""}
            />
            <Box>
              <DataTable<FreeDay, FreedaysFilter>
                  name="table-freedays"
                  queryKey={["freedays", mandantId]}
                  loadData={(params: DataRequestParams<FreedaysFilter>) => loadFreedays(params,request.filter)}
                  request={request}
                  setField={setField as any}
                  columns={useColumns()}
                  size="large"
                  initialFilters={parseInitialFilters(searchParams.get("filter"))}
                  disableRowSelectionOnClick={true}
              />
            </Box>
            <FreedaysBulkCreateDialog
                open={openBulkCreate}
                onClose={() => setOpenBulkCreate(false)}
                onSubmit={async (items) => {
                  try {
                    await addMandantFreedays(mandantId, items);
                    await queryClient.invalidateQueries(["freedays"]);
                    setOpenBulkCreate(false);
                    enqueueSnackbar(
                        translation("freedays-created-success") as string,{variant: "success"}
                    );
                  } catch (e: any) {
                    console.error(e);
                    enqueueSnackbar(
                        translation("freedays-created-failed") as string, {variant:"error"}
                    );
                  }
                }}
            />
          </Paper>
  );
}

function useColumns(): Array<GridColDef<FreeDay>> {
  const { t: translation } = useTranslation("freedays");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const userPermissions = usePermissions();
    const canWrite = userPermissions.includes("freedays:own:delete");
  return useMemo(
    () => [
      {
        field: "datum",
        headerName: capitalize(translation("freeday-attributes-date")),
        flex: 1,
      },
      {
        field: "name",
        headerName: capitalize(translation("freeday-attributes-name")),
        flex: 1,
      },
      {
        field: "countryCode",
        headerName: capitalize(translation("freeday-attributes-country-code")),
        flex: 1,
      },
        ...(canWrite
            ? [
                {
                    field: "actions",
                    headerName: capitalize(translation("freedays-actions")),
                    flex: isMobile ? 1 : 0.5,
                    minWidth: isMobile ? 120 : 100,
                    sortable: false,
                    renderCell: (params: any) => {
                        const freeDay = params.row as FreeDay;
                        return (
                            <Box>
                                <DeleteFreeDaysButton mandantId={freeDay.mandantId} id={freeDay.id}/>
                            </Box>
                        );
                    },
                },
            ]
            : []),
    ],
    [translation,canWrite, isMobile]
  );
}
